import { useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useShowStore } from '../stores/showStore';
import { useConnectionStore } from '../stores/connectionStore';
import { useClockStore } from '../stores/clockStore';
import { LOCAL_FIRST } from '../lib/powersync/db';

const WS_BASE = import.meta.env.VITE_WS_URL || 'ws://localhost:3001';
const RECONNECT_BASE_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;

export function useWebSocket(showId) {
  const wsRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const mountedRef = useRef(true);

  const token = useAuthStore(state => state.token);

  const handleMessage = useCallback((event) => {
    try {
      const { event: eventName, data } = JSON.parse(event.data);
      const { updateCue, updateCueStatus, addCue, removeCue, reorderCues, setCues, resetAllCues, resetSegmentTimer } = useShowStore.getState();
      const { setVmixStatus, setVmixTally } = useConnectionStore.getState();

      // In local-first mode, PowerSync's reactive watch is the source of truth
      // for cue CONTENT and STRUCTURE, so ignore those WS events to avoid
      // double-application. Live-EXECUTION events (status, clock, tally,
      // timer) still ride the WS channel — they carry ephemeral on-air state
      // that isn't in the synced data.
      const CONTENT_EVENTS = new Set([
        'rundown:cue_updated', 'rundown:cue_added', 'rundown:cue_deleted',
        'rundown:reordered', 'rundown:cues_imported', 'rundown:cues_reset',
      ]);
      if (LOCAL_FIRST && CONTENT_EVENTS.has(eventName)) return;

      switch (eventName) {
        case 'connected':
          break;

        case 'subscribed:show':
          break;

        case 'rundown:cue_updated':
          if (data?.cue) updateCue(data.cue);
          break;

        case 'rundown:cue_added':
          if (data?.cue) addCue(data.cue);
          break;

        case 'rundown:cues_imported': {
          if (data?.cues) {
            const existing = useShowStore.getState().cues;
            const incomingIds = new Set(data.cues.map(c => c.id));
            const merged = [
              ...existing.filter(c => !incomingIds.has(c.id)),
              ...data.cues,
            ].sort((a, b) => a.order - b.order);
            setCues(merged);
          }
          break;
        }

        case 'rundown:cue_deleted':
          if (data?.cueId) removeCue(data.cueId);
          break;

        case 'rundown:reordered':
          if (data?.orderedIds) {
            // Block stale reorder broadcasts while a local reorder is in-flight
            if (useShowStore.getState().pendingOpCount > 0) break;
            reorderCues(data.orderedIds);
          }
          break;

        case 'rundown:cue_status_changed':
          if (data?.cueId) {
            // Block WS status updates while any operation is in-flight —
            // late broadcasts from earlier ops would rewind the optimistic UI
            if (useShowStore.getState().pendingOpCount > 0) break;
            updateCueStatus(data.cueId, data.status, {
              actual_duration: data.actual_duration,
              on_air_at: data.on_air_at,
            });
          }
          break;

        case 'rundown:cues_reset':
          resetAllCues();
          break;

        case 'vmix:tally_update':
          setVmixTally({ program: data?.program || [], preview: data?.preview || [] });
          break;

        case 'vmix:connection_status':
          setVmixStatus(data?.connected || false, data?.inputList || []);
          break;

        case 'singular:composition_status':
          // Handled by connection store if needed
          break;

        case 'rundown:cue_actions_started':
          if (data?.cueId) useShowStore.getState().setCueActionsRunning(data.cueId, true);
          break;

        case 'rundown:cue_actions_finished':
          if (data?.cueId) useShowStore.getState().setCueActionsRunning(data.cueId, false);
          break;

        case 'show:clock_update':
          useClockStore.getState().setClockData({
            elapsed: data?.elapsed || 0,
            remaining: data?.remaining ?? null,
            wallClock: data?.wallClock || null,
          });
          break;

        case 'show:status_changed':
          if (data?.status) {
            useShowStore.getState().setShow({
              ...useShowStore.getState().show,
              status: data.status,
              actual_start: data.actual_start || useShowStore.getState().show?.actual_start,
              actual_end: data.actual_end || useShowStore.getState().show?.actual_end,
            });
          }
          break;

        case 'show:timer_reset':
          resetSegmentTimer();
          break;

        default:
          break;
      }
    } catch (err) {
      console.error('WS message handling error:', err);
    }
  }, []);

  const connect = useCallback(() => {
    if (!showId || !token || !mountedRef.current) return;

    const url = `${WS_BASE}/ws?showId=${showId}&token=${encodeURIComponent(token)}`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) {
          ws.close();
          return;
        }

        reconnectAttemptRef.current = 0;
        useConnectionStore.getState().setWsStatus(true, false);

        // Subscribe to show room
        ws.send(JSON.stringify({
          event: 'subscribe:show',
          data: { showId },
        }));
      };

      ws.onmessage = handleMessage;

      ws.onclose = (event) => {
        useConnectionStore.getState().setWsStatus(false, false);

        if (!mountedRef.current) return;

        // Don't reconnect if it was a deliberate close with auth error
        if (event.code === 4001) {
          console.error('WS auth failed, not reconnecting');
          return;
        }

        // Exponential backoff reconnect
        const delay = Math.min(
          RECONNECT_BASE_DELAY * Math.pow(2, reconnectAttemptRef.current),
          MAX_RECONNECT_DELAY
        );
        reconnectAttemptRef.current += 1;

        useConnectionStore.getState().setWsStatus(false, true);
        reconnectTimerRef.current = setTimeout(() => {
          if (mountedRef.current) {
            connect();
          }
        }, delay);
      };

      ws.onerror = (err) => {
        console.error('WebSocket error:', err);
      };
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
    }
  }, [showId, token, handleMessage]);

  useEffect(() => {
    mountedRef.current = true;
    connect();

    return () => {
      mountedRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
        wsRef.current = null;
      }
      useConnectionStore.getState().setWsStatus(false, false);
    };
  }, [connect]);

  const sendMessage = useCallback((event, data) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ event, data }));
      return true;
    }
    return false;
  }, []);

  return { sendMessage };
}
